import { z } from "zod";
import { nativeInvoiceUpdateBodySchema } from "@common/schemas/domains/invoice/native-invoice.schema";
import { updateInvoiceBodySchema } from "@common/schemas/domains/invoice/update-invoice.schema";
import { InvoiceStatus } from "@common/types/invoice";

type UpdateInvoiceBody = z.infer<typeof updateInvoiceBodySchema>;
type NativeInvoiceUpdateBody = z.infer<typeof nativeInvoiceUpdateBodySchema>;

// Single source of truth for "what kind of editor may a user invoke on a
// Kick-native invoice in a given status?". Both the frontend (to show/enable/
// omit mutation controls) and the backend (to gate the corresponding mutation)
// consult this matrix. Do NOT introduce a second status matrix anywhere else —
// adding a new editable capability, or enabling an existing one for another
// status, must be a change to this one map.
//
// Capability checks answer "may the user invoke this kind of editor?"; they are
// distinct from the transition-validity checks in invoice-status-machine.ts,
// which answer "is this specific status transition valid?".
export enum NativeInvoiceCapability {
    FORM_CONTENT = "formContent",
    LINE_CODING = "lineCoding",
    MEMO = "memo",
    STATUS = "status",
    DUE_DATE = "dueDate",
    DOCUMENTS = "documents",
    REVISE = "revise",
    COLLECT_PAYMENT = "collectPayment",
}

type UpdateInvoiceBodyCapabilityField = Exclude<
    keyof UpdateInvoiceBody,
    "revisionInvoiceId"
>;

// Every mutable `updateInvoice` body field must map to a capability so a new
// schema field cannot slip through ungated. revisionInvoiceId is metadata for
// the revision-void flow and is authorized only alongside STATUS.
const UPDATE_INVOICE_BODY_FIELD_CAPABILITIES = {
    memo: NativeInvoiceCapability.MEMO,
    due: NativeInvoiceCapability.DUE_DATE,
    status: NativeInvoiceCapability.STATUS,
    voidedAt: NativeInvoiceCapability.STATUS,
    paidAt: NativeInvoiceCapability.STATUS,
    uncollectableAt: NativeInvoiceCapability.STATUS,
} satisfies Record<UpdateInvoiceBodyCapabilityField, NativeInvoiceCapability>;

const NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES = {
    counterpartyId: NativeInvoiceCapability.FORM_CONTENT,
    financialAccountId: NativeInvoiceCapability.FORM_CONTENT,
    status: NativeInvoiceCapability.FORM_CONTENT,
    renderSnapshot: NativeInvoiceCapability.FORM_CONTENT,
    emailSnapshot: NativeInvoiceCapability.FORM_CONTENT,
    sendingEmailId: NativeInvoiceCapability.FORM_CONTENT,
    termType: NativeInvoiceCapability.FORM_CONTENT,
    customTerm: NativeInvoiceCapability.FORM_CONTENT,
    isEmailSubjectAutomatic: NativeInvoiceCapability.FORM_CONTENT,
    isEmailMessageAutomatic: NativeInvoiceCapability.FORM_CONTENT,
    lineAccounting: NativeInvoiceCapability.LINE_CODING,
    adjustmentSettings: NativeInvoiceCapability.LINE_CODING,
    // Payment settings are not PDF content, so they are editable on OPEN
    // without a Revise — hence a dedicated capability rather than FORM_CONTENT.
    collectPayment: NativeInvoiceCapability.COLLECT_PAYMENT,
    stripeFinancialAccountId: NativeInvoiceCapability.COLLECT_PAYMENT,
    acceptedPaymentMethods: NativeInvoiceCapability.COLLECT_PAYMENT,
} satisfies Record<keyof NativeInvoiceUpdateBody, NativeInvoiceCapability>;

const UNISSUED_INVOICE_CAPABILITIES: readonly NativeInvoiceCapability[] = [
    NativeInvoiceCapability.FORM_CONTENT,
    NativeInvoiceCapability.LINE_CODING,
    NativeInvoiceCapability.MEMO,
    NativeInvoiceCapability.STATUS,
    NativeInvoiceCapability.DUE_DATE,
    NativeInvoiceCapability.DOCUMENTS,
    NativeInvoiceCapability.COLLECT_PAYMENT,
];

const CAPABILITIES_BY_STATUS: Record<
    InvoiceStatus,
    readonly NativeInvoiceCapability[]
> = {
    [InvoiceStatus.DRAFT]: UNISSUED_INVOICE_CAPABILITIES,
    // Open / Paid / Uncollectible open the drawer as a PDF preview only —
    // content, design, and coding are not edited in place. Class / accrual
    // coding still flows through the line-item spreadsheet (LINE_CODING).
    // Content changes for Open use Revise.
    [InvoiceStatus.OPEN]: [
        NativeInvoiceCapability.LINE_CODING,
        NativeInvoiceCapability.MEMO,
        NativeInvoiceCapability.STATUS,
        NativeInvoiceCapability.DUE_DATE,
        NativeInvoiceCapability.DOCUMENTS,
        NativeInvoiceCapability.REVISE,
        NativeInvoiceCapability.COLLECT_PAYMENT,
    ],
    [InvoiceStatus.PAID]: [
        NativeInvoiceCapability.LINE_CODING,
        NativeInvoiceCapability.MEMO,
        NativeInvoiceCapability.STATUS,
        NativeInvoiceCapability.DUE_DATE,
        NativeInvoiceCapability.DOCUMENTS,
    ],
    [InvoiceStatus.UNCOLLECTABLE]: [
        NativeInvoiceCapability.LINE_CODING,
        NativeInvoiceCapability.MEMO,
        NativeInvoiceCapability.STATUS,
        NativeInvoiceCapability.DUE_DATE,
        NativeInvoiceCapability.DOCUMENTS,
    ],
    [InvoiceStatus.VOID]: [NativeInvoiceCapability.MEMO],
};

interface CanUseNativeInvoiceCapabilityParams {
    status: InvoiceStatus;
    capability: NativeInvoiceCapability;
}

export function canUseNativeInvoiceCapability({
    status,
    capability,
}: CanUseNativeInvoiceCapabilityParams): boolean {
    return CAPABILITIES_BY_STATUS[status]?.includes(capability) ?? false;
}

export function getRequiredNativeInvoiceCapabilitiesForUpdateInvoiceBody(
    body: Partial<UpdateInvoiceBody>,
): NativeInvoiceCapability[] {
    const capabilities = new Set<NativeInvoiceCapability>();
    if (body.memo !== undefined) {
        capabilities.add(UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.memo);
    }
    if (body.due !== undefined) {
        capabilities.add(UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.due);
    }
    if (body.status !== undefined) {
        capabilities.add(UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.status);
    }
    if (body.voidedAt !== undefined) {
        capabilities.add(UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.voidedAt);
    }
    if (body.paidAt !== undefined) {
        capabilities.add(UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.paidAt);
    }
    if (body.uncollectableAt !== undefined) {
        capabilities.add(
            UPDATE_INVOICE_BODY_FIELD_CAPABILITIES.uncollectableAt,
        );
    }
    return [...capabilities];
}

export function getRequiredNativeInvoiceCapabilitiesForNativeInvoiceUpdateBody(
    body: Partial<NativeInvoiceUpdateBody>,
): NativeInvoiceCapability[] {
    const capabilities = new Set<NativeInvoiceCapability>();
    if (body.counterpartyId !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.counterpartyId,
        );
    }
    if (body.financialAccountId !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.financialAccountId,
        );
    }
    if (body.status !== undefined) {
        capabilities.add(NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.status);
    }
    if (body.renderSnapshot !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.renderSnapshot,
        );
    }
    if (body.emailSnapshot !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.emailSnapshot,
        );
    }
    if (body.sendingEmailId !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.sendingEmailId,
        );
    }
    if (body.termType !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.termType,
        );
    }
    if (body.customTerm !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.customTerm,
        );
    }
    if (body.isEmailSubjectAutomatic !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.isEmailSubjectAutomatic,
        );
    }
    if (body.isEmailMessageAutomatic !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.isEmailMessageAutomatic,
        );
    }
    if (body.lineAccounting !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.lineAccounting,
        );
    }
    if (body.adjustmentSettings !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.adjustmentSettings,
        );
    }
    if (body.collectPayment !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.collectPayment,
        );
    }
    if (body.stripeFinancialAccountId !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.stripeFinancialAccountId,
        );
    }
    if (body.acceptedPaymentMethods !== undefined) {
        capabilities.add(
            NATIVE_INVOICE_UPDATE_BODY_FIELD_CAPABILITIES.acceptedPaymentMethods,
        );
    }
    return [...capabilities];
}
